import React from 'react';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { money } from './review';
import type { CoveredAmount, HealthProperty, ReviewWindow } from './types';

function Amount({ amount }: { amount?: CoveredAmount }) {
  const value = amount?.value ?? amount?.observed_subtotal;
  return (
    <Box sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      <Tooltip title={value == null ? 'No margin observations for this window' : 'AUD, after refunds'}>
        <Typography variant="body2" fontWeight={600} color={value == null ? 'text.secondary' : 'text.primary'}>
          {value == null ? '—' : money(value)}
        </Typography>
      </Tooltip>
      {amount?.value == null && value != null && (
        <Typography variant="caption" color="text.secondary">
          Observed subtotal
        </Typography>
      )}
    </Box>
  );
}

export default function HotelPortfolioTable({
  properties,
  window,
  comparisonEndDate,
  onReview,
  onClear,
}: {
  properties: HealthProperty[];
  window: ReviewWindow;
  comparisonEndDate?: string;
  onReview: (id: string) => void;
  onClear: () => void;
}) {
  return (
    <TableContainer
      role="region"
      aria-label="Scrollable hotel list"
      tabIndex={0}
      sx={{
        maxHeight: 'clamp(360px, 52vh, 600px)',
        overflow: 'auto',
        overscrollBehavior: 'contain',
        scrollbarGutter: 'stable',
        isolation: 'isolate',
      }}
    >
      <Table
        size="small"
        stickyHeader
        aria-label="LPC hotel portfolio"
        sx={{ '& td': { py: 1.75 }, '& th': { whiteSpace: 'nowrap' } }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Hotel</TableCell>
            <TableCell>Commercial contact</TableCell>
            <TableCell align="right">LPC margin</TableCell>
            <TableCell align="right">LPP margin</TableCell>
            <TableCell align="right">
              <Tooltip
                title={`Seven days ending ${comparisonEndDate ?? 'on the selected end date'} compared with the preceding seven days, using matched financial observations`}
              >
                <span>7-day LPC margin change</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {properties.map((property) => {
            const metrics = property.windows?.[window];
            const movement = metrics?.margin_change_7d_aud.value;
            const hasMovement = typeof movement === 'number' && Number.isFinite(movement);
            return (
              <TableRow
                key={property.property_id}
                hover
                aria-label={`View hotel details for ${property.property_name}`}
                onClick={() => onReview(property.property_id)}
                sx={{
                  cursor: 'pointer',
                  '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                }}
              >
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {property.property_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {property.country || 'Country not supplied'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {property.market_manager || 'Not supplied'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Amount amount={metrics?.lpc_margin_ar_aud} />
                </TableCell>
                <TableCell align="right">
                  <Amount amount={metrics?.lpp_margin_ar_aud} />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <Tooltip
                    title={
                      hasMovement
                        ? 'LPC margin after refunds; matched seven-day periods'
                        : 'Not enough comparable financial history'
                    }
                  >
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color={hasMovement && movement > 0 ? 'primary.main' : 'text.secondary'}
                    >
                      {hasMovement ? `${movement > 0 ? '↗ +' : movement < 0 ? '↘ ' : ''}${money(movement)}` : '—'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    aria-label={`Review ${property.property_name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onReview(property.property_id);
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    View hotel →
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {!properties.length && (
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 5, textAlign: 'center' }}>
                No hotels match these filters. <Button onClick={onClear}>Clear filters</Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
